/**
 * Strings for the Socratic exam (pages/Exam*.tsx, components/exam/) and its
 * settings section (components/settings/AiSection.tsx).
 *
 * Spread into the main catalog; `EXAM_EN` is typed against `EXAM_ES`, so a
 * missing English string fails to compile.
 */

export const EXAM_ES = {
  'nav.exam': 'Examen',

  // ── Ajustes → IA ──────────────────────────────────────────────────────────
  'settings.ai.title': 'IA del examen',
  'settings.ai.description': 'El Examen Socrático usa tu propia clave de API. Vive solo en este dispositivo: no se sincroniza y cada dispositivo necesita la suya.',
  'settings.ai.provider': 'Proveedor',
  'settings.ai.provider.anthropic': 'Anthropic',
  'settings.ai.provider.gemini': 'Gemini',
  'settings.ai.provider.compatible': 'Compatible con OpenAI',
  'settings.ai.server': 'Servidor',
  'settings.ai.serverHint': 'OpenAI, OpenRouter, Groq, DeepSeek, Mistral o un servidor local. Los locales (Ollama, LM Studio) no necesitan clave, pero tienen que permitir CORS desde esta página.',
  'settings.ai.server.custom': 'Otro…',
  'settings.ai.baseUrl': 'URL base',
  'settings.ai.model': 'Modelo',
  'settings.ai.modelHint': '«Probar conexión» lista los modelos que tu clave puede usar.',
  'settings.ai.modelPlaceholder': 'Elige o escribe un modelo',
  'settings.ai.key': 'Clave de API',
  'settings.ai.keyPlaceholder': 'Pega tu clave',
  'settings.ai.keyLocalOptional': 'Opcional para servidores locales.',
  'settings.ai.show': 'Mostrar',
  'settings.ai.hide': 'Ocultar',
  'settings.ai.storage': 'Dónde se guarda la clave',
  'settings.ai.remember': 'En este dispositivo',
  'settings.ai.sessionOnly': 'Solo esta sesión',
  'settings.ai.storageHint': '«Solo esta sesión» la olvida al cerrar la pestaña.',
  'settings.ai.risk': 'La clave vive solo en este navegador. Un script que llegara a ejecutarse en la página podría leerla: por eso QUIPWA escapa todo el contenido de tarjetas y respuestas y aplica una política de seguridad (CSP) que solo deja hablar con los proveedores de esta lista. Aun así, usa una clave con tope de gasto.',
  'settings.ai.keysLink': 'Crear una clave',
  'settings.ai.limitsLink': 'Poner un tope de gasto',
  'settings.ai.save': 'Guardar',
  'settings.ai.saved': 'Guardado en este dispositivo.',
  'settings.ai.test': 'Probar conexión',
  'settings.ai.testing': 'Probando…',
  'settings.ai.testOk': 'Conexión correcta: {count} modelos disponibles.',
  'settings.ai.testOkOne': 'Conexión correcta: 1 modelo disponible.',
  'settings.ai.testFail': 'No funcionó: {error}',
  'settings.ai.forget': 'Quitar la clave',
  'settings.ai.forgotten': 'Clave quitada de este dispositivo.',
  'settings.ai.priceKnown': 'Precio de referencia: {input} $ / {output} $ por millón de tokens (entrada / salida).',
  'settings.ai.priceUnknown': 'Sin precio de referencia para este modelo: el examen mostrará tokens en lugar de dinero.',

  // ── Inicio del examen ────────────────────────────────────────────────────
  'exam.home.title': 'Examen socrático',
  'exam.home.lead': 'Preguntas que te piden explicar, no reconocer. Respondes con tus palabras, dices cuánto confías y una IA te corrige con la rúbrica de cada pregunta.',
  'exam.aiNotice': 'Te corrige una IA. Es práctica, no certificación.',
  'exam.noKey.title': 'Falta conectar un modelo en este dispositivo',
  'exam.noKey.body': 'El examen usa tu propia clave de API: Anthropic, Gemini o cualquier servidor compatible con OpenAI. Las claves no se sincronizan, así que cada dispositivo necesita la suya.',
  'exam.noKey.action': 'Configurar en Ajustes',
  'exam.home.start': 'Empezar sesión · {count} conceptos',
  'exam.home.startOne': 'Empezar sesión · 1 concepto',
  'exam.home.nothingDue': 'Nada que repasar ahora. Vuelve cuando venza un concepto, o crea un banco desde otra nota.',
  'exam.home.stats': '{concepts} conceptos · {mastered} dominados · deuda {debt}',
  'exam.home.banks': 'Bancos por nota',
  'exam.home.bankRow': '{verified} aprobadas · {drafts} por revisar',
  'exam.home.empty.title': 'Aún no hay preguntas',
  'exam.home.empty.body': 'Abre una nota y pulsa el botón del examen para crear su banco de preguntas. Revisas cada pregunta antes de que entre al examen.',
  'exam.home.empty.action': 'Ir a Notas',
  'exam.home.missingNote': 'Nota que ya no está en este dispositivo',

  // ── Grupos: de qué examinarse ────────────────────────────────────────────
  'exam.scope.all': 'Todo el examen',
  'exam.scope.kind.note': 'Nota',
  'exam.scope.kind.course': 'Curso',
  'exam.scope.kind.tag': 'Etiqueta',
  'exam.scope.kind.domain': 'Dominio',
  'exam.scope.kind.notes': 'Selección',
  'exam.scope.selection': '{count} notas elegidas',
  'exam.home.backAll': 'Todo el examen',
  'exam.home.courses': 'Por curso',
  'exam.home.domains': 'Por dominio',
  'exam.home.tags': 'Por etiqueta',
  'exam.home.longCourse': 'Curso largo · {count} módulos',
  'exam.home.longCourseOne': 'Curso largo · 1 módulo',
  'exam.home.shortCourse': 'Curso corto',
  'exam.home.groupDue': '{due} para hoy',
  'exam.home.groupMastered': '{mastered} de {concepts} dominados',
  'exam.home.groupStart': 'Empezar',
  'exam.home.groupStartLabel': 'Empezar sesión: {name}',
  'exam.home.tagDue': '{name}: {due} para hoy',
  'exam.home.pickHint': 'Marca varias notas para examinarte de todas a la vez.',
  'exam.home.pickNote': 'Elegir {name}',
  'exam.home.pickUse': 'Examinarme de {count} notas',
  'exam.home.pickUseOne': 'Examinarme de 1 nota',
  'exam.home.pickClear': 'Desmarcar',
  'exam.home.scopeNotes': 'Notas de este grupo',
  'exam.home.noBankRow': 'Sin preguntas · crear banco',
  'exam.home.scopeNothingDue': 'Nada de este grupo vence ahora.',
  'exam.home.scopeNoBank.title': 'Este grupo aún no tiene preguntas aprobadas',
  'exam.home.scopeNoBank.body': 'Abre una de sus notas para crear su banco y aprobar sus preguntas. Solo las aprobadas entran al examen.',
  'exam.course.start': 'Examen de este curso',

  // ── Banco de una nota ────────────────────────────────────────────────────
  'exam.bank.title': 'Banco de preguntas',
  'exam.bank.lead': 'Solo las preguntas aprobadas entran al examen. Una pregunta se puede aprobar cuando su cita está en tu nota, palabra por palabra.',
  'exam.bank.generate': 'Crear banco con IA',
  'exam.bank.generateMore': 'Generar más preguntas',
  'exam.bank.generating': 'Escribiendo preguntas a partir de tu nota. Puede tardar un minuto…',
  'exam.bank.generated': '{count} preguntas nuevas para revisar.',
  'exam.bank.generatedOne': '1 pregunta nueva para revisar.',
  'exam.bank.generatedNone': 'El modelo no encontró nada que preguntar en esta nota.',
  'exam.bank.approveAll': 'Aprobar las {count} con cita verificada',
  'exam.bank.approveOne': 'Aprobar la que tiene la cita verificada',
  'exam.bank.approve': 'Aprobar',
  'exam.bank.unapprove': 'Volver a revisar',
  'exam.bank.discard': 'Descartar',
  'exam.bank.quoteFound': 'Cita encontrada en la nota',
  'exam.bank.quoteMissing': 'Cita no encontrada en la nota: no se puede aprobar',
  'exam.bank.singleVariant': 'Solo una variante: no habrá repregunta',
  'exam.bank.reference': 'Respuesta de referencia',
  'exam.bank.quote': 'Cita de la nota',
  'exam.bank.rubric': 'Rúbrica',
  'exam.bank.hints': 'Pistas',
  'exam.bank.details': 'Ver respuesta, rúbrica y pistas',
  'exam.bank.status.draft': 'Por revisar',
  'exam.bank.status.verified': 'Aprobada',
  'exam.bank.variant': 'Variante {n}',
  'exam.bank.start': 'Examen de esta nota',
  'exam.bank.back': 'Volver a la nota',
  'exam.bank.noteMissing': 'Esta nota no está en este dispositivo.',
  'exam.bank.emptyNote': 'La nota está vacía: escribe algo antes de crear preguntas.',
  'exam.bank.empty': 'Esta nota aún no tiene preguntas.',
  'exam.bank.spend': 'Costo estimado de la generación: {cost}',
  'exam.bank.spendTokens': 'Generación: {tokens} tokens (sin precio de referencia)',

  'exam.bloom.1': 'Recordar',
  'exam.bloom.2': 'Comprender',
  'exam.bloom.3': 'Aplicar',
  'exam.bloom.4': 'Analizar',
  'exam.bloom.5': 'Evaluar',
  'exam.bloom.6': 'Crear',
  'exam.solo.1': 'Preestructural',
  'exam.solo.2': 'Uniestructural',
  'exam.solo.3': 'Multiestructural',
  'exam.solo.4': 'Relacional',
  'exam.solo.5': 'Abstracto ampliado',

  // ── Sesión ───────────────────────────────────────────────────────────────
  'exam.session.progress': 'Pregunta {n} de {total}',
  'exam.session.end': 'Salir',
  'exam.session.pause': 'Pausar',
  'exam.session.resume': 'Reanudar',
  'exam.session.paused.title': 'Examen en pausa',
  'exam.session.paused.body': 'La pregunta queda oculta y el micrófono, apagado, hasta que vuelvas; al reanudar se lee otra vez. Si sales, lo que ya respondiste está guardado y esta pregunta no cuenta.',
  'exam.session.loading': 'Preparando la sesión…',
  'exam.session.question': 'Pregunta',
  'exam.session.yourAnswer': 'Tu respuesta',
  'exam.session.answerPlaceholder': 'Explícalo con tus palabras: qué es, por qué ocurre, cómo se conecta con lo demás.',
  'exam.session.continue': 'Seguir',
  'exam.session.hint': 'Pedir pista',
  'exam.session.hintN': 'Pista {n} de 4',
  'exam.session.skip': 'Saltar',
  'exam.session.showAnswer': 'Ver la respuesta',
  'exam.session.confidence': 'Tu confianza',
  'exam.session.confidence.title': '¿Cuánto confías en tu respuesta?',
  'exam.session.confidence.why': 'Se fija antes de la corrección: así se ve si tu seguridad acierta.',
  'exam.session.confidence.1': 'Adivino',
  'exam.session.confidence.2': 'Dudo',
  'exam.session.confidence.3': 'Quizá',
  'exam.session.confidence.4': 'Seguro',
  'exam.session.confidence.5': 'Certeza',
  'exam.session.grade': 'Corregir',
  'exam.session.chooseConfidence': 'Elige tu confianza para corregir',
  'exam.session.evaluating': 'Corrigiendo…',
  'exam.session.counted': 'Cuenta, con tus palabras',
  'exam.session.review': 'Revisa',
  'exam.session.missing': 'Falta: {ideas} ideas y {relations} relaciones de la rúbrica',
  'exam.session.nothingCounted': 'Todavía no hay nada que acreditar.',
  'exam.session.verdict.success': 'Llegaste al nivel que pide la pregunta.',
  'exam.session.verdict.retry': 'Todavía no llega al nivel que pide la pregunta. Pide una pista o mira la respuesta.',
  'exam.session.verdict.exhausted': 'Con las 4 pistas usadas, toca ver la respuesta.',
  'exam.session.verdict.confident_error': 'Estabas seguro y no era así. Mira la referencia: otra variante volverá más adelante.',
  'exam.session.verdict.explained': 'Autoexplicación corregida.',
  'exam.session.soloTarget': 'Nivel SOLO: {level} · esta pregunta pide {target}',
  'exam.session.evaluator': 'Corrigió {model}',
  'exam.session.uncertain': 'El evaluador no estaba seguro: tómalo con cautela.',
  'exam.session.dispute': 'No estoy de acuerdo',
  'exam.session.disputeReason': 'Qué crees que falló (opcional, solo para ti)',
  'exam.session.disputeSave': 'Guardar disputa',
  'exam.session.disputeSaved': 'Disputa guardada. La revisarás en frío más adelante.',
  'exam.session.cancel': 'Cancelar',
  'exam.session.next': 'Siguiente',
  'exam.session.explainWhy': 'Explicar por qué',
  'exam.session.selfExplain.title': '¿Por qué es así?',
  'exam.session.selfExplain.lead': 'Explícalo en 2 o 3 frases, como si se lo contaras a alguien que no lo sabe.',
  'exam.session.selfExplain.send': 'Enviar',
  'exam.session.reveal.title': 'Así lo explica tu nota',
  'exam.session.reveal.rubric': 'Lo que pedía la pregunta',
  'exam.session.error.title': 'No se pudo corregir',
  'exam.session.error.kept': 'Tu respuesta y tu confianza siguen aquí.',
  'exam.session.error.retry': 'Reintentar',
  'exam.session.error.setAside': 'Apartar esta pregunta',
  'exam.session.error.setAsideHint': 'No cuenta como salto: volverá en otra sesión.',
  'exam.session.error.settings': 'Revisar Ajustes',
  'exam.session.nothing': 'No hay preguntas para esta sesión.',
  'exam.session.noBank': 'Este grupo no tiene preguntas aprobadas.',
  'exam.session.badScope': 'Este enlace de examen no es válido.',

  'exam.error.no_key': 'No hay un modelo configurado en este dispositivo.',
  'exam.error.auth': 'El proveedor rechazó la clave.',
  'exam.error.rate_limit': 'Se alcanzó el límite de uso del proveedor. Espera un poco y reintenta.',
  'exam.error.network': 'No hay conexión con el proveedor.',
  'exam.error.refused': 'El modelo rehusó responder.',
  'exam.error.truncated': 'La respuesta del modelo se cortó.',
  'exam.error.bad_output': 'El modelo no devolvió el formato pedido.',
  'exam.error.provider': 'El proveedor devolvió un error.',
  'exam.error.unknown': 'Error inesperado.',

  // ── Cierre ───────────────────────────────────────────────────────────────
  'exam.summary.title': 'Sesión terminada',
  'exam.summary.sentence': '{success} de {total} preguntas alcanzaron su nivel.',
  'exam.summary.none': 'No se respondió ninguna pregunta.',
  'exam.summary.concept': 'Concepto',
  'exam.summary.level': 'Nivel',
  'exam.summary.solo': 'SOLO',
  'exam.summary.hints': 'Pistas',
  'exam.summary.confidence': 'Confianza',
  'exam.summary.result': 'Resultado',
  'exam.summary.next': 'Vuelve',
  'exam.summary.outcome.ok': 'Acierto',
  'exam.summary.outcome.hinted': 'Con pistas',
  'exam.summary.outcome.exit': 'Salida',
  'exam.summary.outcome.skip': 'Salto',
  'exam.summary.confidentError': 'error con seguridad',
  'exam.summary.today': 'Hoy',
  'exam.summary.tomorrow': 'Mañana',
  'exam.summary.inDays': 'En {n} días',
  'exam.summary.cost': 'Costo estimado: {cost} · correcciones: {calls} · tokens: {tokens}',
  'exam.summary.costUnknown': 'Correcciones: {calls} · tokens: {tokens} (sin precio de referencia para el modelo)',
  'exam.summary.disputes': 'Disputas guardadas para revisar: {count}.',
  'exam.summary.back': 'Volver al examen',
  'exam.summary.again': 'Otra sesión',
} as const

export const EXAM_EN: Record<keyof typeof EXAM_ES, string> = {
  'nav.exam': 'Exam',

  'settings.ai.title': 'Exam AI',
  'settings.ai.description': 'The Socratic Exam uses your own API key. It lives on this device only: it does not sync, and each device needs its own.',
  'settings.ai.provider': 'Provider',
  'settings.ai.provider.anthropic': 'Anthropic',
  'settings.ai.provider.gemini': 'Gemini',
  'settings.ai.provider.compatible': 'OpenAI-compatible',
  'settings.ai.server': 'Server',
  'settings.ai.serverHint': 'OpenAI, OpenRouter, Groq, DeepSeek, Mistral or a local server. Local ones (Ollama, LM Studio) need no key, but they must allow CORS from this page.',
  'settings.ai.server.custom': 'Other…',
  'settings.ai.baseUrl': 'Base URL',
  'settings.ai.model': 'Model',
  'settings.ai.modelHint': '“Test connection” lists the models your key can use.',
  'settings.ai.modelPlaceholder': 'Pick or type a model',
  'settings.ai.key': 'API key',
  'settings.ai.keyPlaceholder': 'Paste your key',
  'settings.ai.keyLocalOptional': 'Optional for local servers.',
  'settings.ai.show': 'Show',
  'settings.ai.hide': 'Hide',
  'settings.ai.storage': 'Where the key is kept',
  'settings.ai.remember': 'On this device',
  'settings.ai.sessionOnly': 'This session only',
  'settings.ai.storageHint': '“This session only” forgets it when the tab closes.',
  'settings.ai.risk': 'The key lives only in this browser. A script that managed to run on the page could read it: that is why QUIPWA escapes all card and answer content and enforces a security policy (CSP) that only lets it talk to the providers on this list. Even so, use a key with a spending limit.',
  'settings.ai.keysLink': 'Create a key',
  'settings.ai.limitsLink': 'Set a spending limit',
  'settings.ai.save': 'Save',
  'settings.ai.saved': 'Saved on this device.',
  'settings.ai.test': 'Test connection',
  'settings.ai.testing': 'Testing…',
  'settings.ai.testOk': 'Connected: {count} models available.',
  'settings.ai.testOkOne': 'Connected: 1 model available.',
  'settings.ai.testFail': 'It did not work: {error}',
  'settings.ai.forget': 'Remove the key',
  'settings.ai.forgotten': 'Key removed from this device.',
  'settings.ai.priceKnown': 'Reference price: ${input} / ${output} per million tokens (input / output).',
  'settings.ai.priceUnknown': 'No reference price for this model: the exam will show tokens instead of money.',

  'exam.home.title': 'Socratic exam',
  'exam.home.lead': 'Questions that ask you to explain, not to recognise. You answer in your own words, say how confident you are, and an AI grades you against each question’s rubric.',
  'exam.aiNotice': 'An AI grades you. It is practice, not certification.',
  'exam.noKey.title': 'No model connected on this device',
  'exam.noKey.body': 'The exam uses your own API key: Anthropic, Gemini or any OpenAI-compatible server. Keys do not sync, so each device needs its own.',
  'exam.noKey.action': 'Set up in Settings',
  'exam.home.start': 'Start session · {count} concepts',
  'exam.home.startOne': 'Start session · 1 concept',
  'exam.home.nothingDue': 'Nothing due right now. Come back when a concept is due, or create a bank from another note.',
  'exam.home.stats': '{concepts} concepts · {mastered} mastered · debt {debt}',
  'exam.home.banks': 'Banks by note',
  'exam.home.bankRow': '{verified} approved · {drafts} to review',
  'exam.home.empty.title': 'No questions yet',
  'exam.home.empty.body': 'Open a note and press the exam button to create its question bank. You review each question before it enters the exam.',
  'exam.home.empty.action': 'Go to Notes',
  'exam.home.missingNote': 'Note no longer on this device',

  'exam.scope.all': 'The whole exam',
  'exam.scope.kind.note': 'Note',
  'exam.scope.kind.course': 'Course',
  'exam.scope.kind.tag': 'Tag',
  'exam.scope.kind.domain': 'Domain',
  'exam.scope.kind.notes': 'Selection',
  'exam.scope.selection': '{count} chosen notes',
  'exam.home.backAll': 'The whole exam',
  'exam.home.courses': 'By course',
  'exam.home.domains': 'By domain',
  'exam.home.tags': 'By tag',
  'exam.home.longCourse': 'Long course · {count} modules',
  'exam.home.longCourseOne': 'Long course · 1 module',
  'exam.home.shortCourse': 'Short course',
  'exam.home.groupDue': '{due} due',
  'exam.home.groupMastered': '{mastered} of {concepts} mastered',
  'exam.home.groupStart': 'Start',
  'exam.home.groupStartLabel': 'Start session: {name}',
  'exam.home.tagDue': '{name}: {due} due',
  'exam.home.pickHint': 'Tick several notes to be examined on all of them at once.',
  'exam.home.pickNote': 'Choose {name}',
  'exam.home.pickUse': 'Exam on {count} notes',
  'exam.home.pickUseOne': 'Exam on 1 note',
  'exam.home.pickClear': 'Clear',
  'exam.home.scopeNotes': 'Notes in this group',
  'exam.home.noBankRow': 'No questions · create a bank',
  'exam.home.scopeNothingDue': 'Nothing in this group is due now.',
  'exam.home.scopeNoBank.title': 'This group has no approved questions yet',
  'exam.home.scopeNoBank.body': 'Open one of its notes to create its bank and approve its questions. Only approved questions enter the exam.',
  'exam.course.start': 'Exam on this course',

  'exam.bank.title': 'Question bank',
  'exam.bank.lead': 'Only approved questions enter the exam. A question can be approved when its quote is in your note, word for word.',
  'exam.bank.generate': 'Create bank with AI',
  'exam.bank.generateMore': 'Generate more questions',
  'exam.bank.generating': 'Writing questions from your note. This can take a minute…',
  'exam.bank.generated': '{count} new questions to review.',
  'exam.bank.generatedOne': '1 new question to review.',
  'exam.bank.generatedNone': 'The model found nothing to ask in this note.',
  'exam.bank.approveAll': 'Approve the {count} with a verified quote',
  'exam.bank.approveOne': 'Approve the one with a verified quote',
  'exam.bank.approve': 'Approve',
  'exam.bank.unapprove': 'Back to review',
  'exam.bank.discard': 'Discard',
  'exam.bank.quoteFound': 'Quote found in the note',
  'exam.bank.quoteMissing': 'Quote not found in the note: cannot be approved',
  'exam.bank.singleVariant': 'Only one variant: no re-question',
  'exam.bank.reference': 'Reference answer',
  'exam.bank.quote': 'Quote from the note',
  'exam.bank.rubric': 'Rubric',
  'exam.bank.hints': 'Hints',
  'exam.bank.details': 'See answer, rubric and hints',
  'exam.bank.status.draft': 'To review',
  'exam.bank.status.verified': 'Approved',
  'exam.bank.variant': 'Variant {n}',
  'exam.bank.start': 'Exam on this note',
  'exam.bank.back': 'Back to the note',
  'exam.bank.noteMissing': 'This note is not on this device.',
  'exam.bank.emptyNote': 'The note is empty: write something before creating questions.',
  'exam.bank.empty': 'This note has no questions yet.',
  'exam.bank.spend': 'Estimated generation cost: {cost}',
  'exam.bank.spendTokens': 'Generation: {tokens} tokens (no reference price)',

  'exam.bloom.1': 'Remember',
  'exam.bloom.2': 'Understand',
  'exam.bloom.3': 'Apply',
  'exam.bloom.4': 'Analyse',
  'exam.bloom.5': 'Evaluate',
  'exam.bloom.6': 'Create',
  'exam.solo.1': 'Prestructural',
  'exam.solo.2': 'Unistructural',
  'exam.solo.3': 'Multistructural',
  'exam.solo.4': 'Relational',
  'exam.solo.5': 'Extended abstract',

  'exam.session.progress': 'Question {n} of {total}',
  'exam.session.end': 'Leave',
  'exam.session.pause': 'Pause',
  'exam.session.resume': 'Resume',
  'exam.session.paused.title': 'Exam paused',
  'exam.session.paused.body': 'The question stays hidden and the microphone off until you are back; on resuming it is read again. If you leave, what you already answered is saved and this question does not count.',
  'exam.session.loading': 'Preparing the session…',
  'exam.session.question': 'Question',
  'exam.session.yourAnswer': 'Your answer',
  'exam.session.answerPlaceholder': 'Explain it in your own words: what it is, why it happens, how it connects to the rest.',
  'exam.session.continue': 'Continue',
  'exam.session.hint': 'Ask for a hint',
  'exam.session.hintN': 'Hint {n} of 4',
  'exam.session.skip': 'Skip',
  'exam.session.showAnswer': 'Show the answer',
  'exam.session.confidence': 'Your confidence',
  'exam.session.confidence.title': 'How confident are you in your answer?',
  'exam.session.confidence.why': 'It is locked before grading, so you can see whether your confidence is right.',
  'exam.session.confidence.1': 'Guess',
  'exam.session.confidence.2': 'Unsure',
  'exam.session.confidence.3': 'Maybe',
  'exam.session.confidence.4': 'Sure',
  'exam.session.confidence.5': 'Certain',
  'exam.session.grade': 'Grade',
  'exam.session.chooseConfidence': 'Choose your confidence to grade',
  'exam.session.evaluating': 'Grading…',
  'exam.session.counted': 'Counted, in your words',
  'exam.session.review': 'Check',
  'exam.session.missing': 'Missing: {ideas} ideas and {relations} relations from the rubric',
  'exam.session.nothingCounted': 'Nothing to credit yet.',
  'exam.session.verdict.success': 'You reached the level the question asks for.',
  'exam.session.verdict.retry': 'Not yet at the level the question asks for. Ask for a hint or see the answer.',
  'exam.session.verdict.exhausted': 'All 4 hints used: time to see the answer.',
  'exam.session.verdict.confident_error': 'You were sure and it was not right. Read the reference: another variant will come back later.',
  'exam.session.verdict.explained': 'Self-explanation graded.',
  'exam.session.soloTarget': 'SOLO level: {level} · this question asks for {target}',
  'exam.session.evaluator': 'Graded by {model}',
  'exam.session.uncertain': 'The evaluator was not sure: take it with care.',
  'exam.session.dispute': 'I disagree',
  'exam.session.disputeReason': 'What do you think went wrong (optional, only for you)',
  'exam.session.disputeSave': 'Save dispute',
  'exam.session.disputeSaved': 'Dispute saved. You will review it with a cool head later.',
  'exam.session.cancel': 'Cancel',
  'exam.session.next': 'Next',
  'exam.session.explainWhy': 'Explain why',
  'exam.session.selfExplain.title': 'Why is it so?',
  'exam.session.selfExplain.lead': 'Explain it in 2 or 3 sentences, as if to someone who does not know it.',
  'exam.session.selfExplain.send': 'Send',
  'exam.session.reveal.title': 'How your note explains it',
  'exam.session.reveal.rubric': 'What the question asked for',
  'exam.session.error.title': 'Could not grade',
  'exam.session.error.kept': 'Your answer and your confidence are still here.',
  'exam.session.error.retry': 'Retry',
  'exam.session.error.setAside': 'Set this question aside',
  'exam.session.error.setAsideHint': 'It does not count as a skip: it comes back in another session.',
  'exam.session.error.settings': 'Check Settings',
  'exam.session.nothing': 'No questions for this session.',
  'exam.session.noBank': 'This group has no approved questions.',
  'exam.session.badScope': 'This exam link is not valid.',

  'exam.error.no_key': 'No model is set up on this device.',
  'exam.error.auth': 'The provider rejected the key.',
  'exam.error.rate_limit': 'The provider’s usage limit was reached. Wait a little and retry.',
  'exam.error.network': 'No connection to the provider.',
  'exam.error.refused': 'The model declined to answer.',
  'exam.error.truncated': 'The model’s reply was cut short.',
  'exam.error.bad_output': 'The model did not return the requested format.',
  'exam.error.provider': 'The provider returned an error.',
  'exam.error.unknown': 'Unexpected error.',

  'exam.summary.title': 'Session finished',
  'exam.summary.sentence': '{success} of {total} questions reached their level.',
  'exam.summary.none': 'No question was answered.',
  'exam.summary.concept': 'Concept',
  'exam.summary.level': 'Level',
  'exam.summary.solo': 'SOLO',
  'exam.summary.hints': 'Hints',
  'exam.summary.confidence': 'Confidence',
  'exam.summary.result': 'Result',
  'exam.summary.next': 'Back',
  'exam.summary.outcome.ok': 'Correct',
  'exam.summary.outcome.hinted': 'With hints',
  'exam.summary.outcome.exit': 'Gave up',
  'exam.summary.outcome.skip': 'Skipped',
  'exam.summary.confidentError': 'confident error',
  'exam.summary.today': 'Today',
  'exam.summary.tomorrow': 'Tomorrow',
  'exam.summary.inDays': 'In {n} days',
  'exam.summary.cost': 'Estimated cost: {cost} · gradings: {calls} · tokens: {tokens}',
  'exam.summary.costUnknown': 'Gradings: {calls} · tokens: {tokens} (no reference price for the model)',
  'exam.summary.disputes': 'Disputes saved to review: {count}.',
  'exam.summary.back': 'Back to the exam',
  'exam.summary.again': 'Another session',
}
